import React, { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { RightSidebar } from '@/components/ui/RightSidebar';
import { useSidePanelStore } from '@/stores/sidePanelStore';

const AdoptedNode: React.FC<{ node: HTMLElement; hidden: boolean }> = ({
  node,
  hidden,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.appendChild(node);
    return () => {
      if (node.parentNode === host) host.removeChild(node);
    };
  }, [node]);
  return (
    <div
      data-panel-slot=""
      ref={hostRef}
      className="min-w-0"
      style={hidden ? { display: 'none' } : undefined}
    />
  );
};

/** The app's single side-panel host. Every `SidePanel` renders through it. */
export const SidePanelRoot: React.FC = () => {
  const stack = useSidePanelStore(useShallow((s) => s.stack));
  const closeTop = useSidePanelStore((s) => s.closeTop);
  const closeAll = useSidePanelStore((s) => s.closeAll);

  const open = stack.filter((e) => !e.closing);
  const top = open[open.length - 1] ?? stack[stack.length - 1];

  return (
    <RightSidebar
      title={top?.title ?? ''}
      isVisible={open.length > 0}
      onClose={closeAll}
      onBack={open.length > 1 ? closeTop : undefined}
      width={top?.width}
      footer={
        top?.hasFooter ? (
          <AdoptedNode node={top.footerNode} hidden={false} />
        ) : undefined
      }
    >
      {stack.map((entry) => (
        <AdoptedNode
          key={entry.id}
          node={entry.bodyNode}
          hidden={entry.id !== top?.id}
        />
      ))}
    </RightSidebar>
  );
};

export default SidePanelRoot;
